import * as React from "react";
import styled from "@emotion/styled";
import { FlexRow, H3, ReviewTag, ScreenReaderText, Text } from "atoms";

type TagName = React.ComponentProps<typeof ReviewTag>["variant"];
type SortedTags = [string, number][];

interface Props {
  type: "ARTIST" | "PROJECT";
  max?: number;
  ratings: {
    [key: string]: {
      value: number;
      icon: React.ReactNode;
    };
  };

  tags: {
    [Property in TagName]?: SortedTags;
  };
}

function ReviewStats({ type, ratings, tags, max, ...props }: Props) {
  return (
    <Section {...props}>
      <SH3>Reaction overview</SH3>
      <ReactionList>
        {Object.entries(ratings).map(([key, data]) => {
          const orderValue = data.value * -1;
          if (type === "ARTIST" && data.value > 0) {
            return (
              <ReactionItem order={orderValue} key={`artist-${key}`}>
                {data.icon}
                <Text size="xs">
                  <strong>{data.value}</strong>
                  <span>{key}</span>
                </Text>
              </ReactionItem>
            );
          }

          if (type === "PROJECT") {
            const mathMax = max || 0;
            const reviewCountText = `${data.value ?? 0} review${
              (data.value ?? 0) === 1 ? "" : "s"
            }`;
            return (
              <ReactionItem key={`project-${key}`}>
                {data.icon}
                <ScreenReaderText>{data.value}</ScreenReaderText>
                <div>
                  <RatingBar value={data.value} max={max as number} />
                  <FlexRow justifyContent="space-between">
                    <Text size="xxxs">{reviewCountText}</Text>
                    {data.value > 0 ? (
                      <Text size="xxxs">
                        {Math.round((data.value / mathMax) * 100)}%
                      </Text>
                    ) : (
                      <Text size="xxxs">0%</Text>
                    )}
                  </FlexRow>
                </div>
              </ReactionItem>
            );
          }
          return null;
        })}
      </ReactionList>

      <TopTags>
        {Object.entries(tags).map(([tagName, tagStats]) =>
          !tagStats[0] ? null : (
            <ReviewTag
              variant={tagName as TagName}
              count={tagStats[0][1]}
              key={tagName as TagName}
            >
              {tagStats[0][0]}
            </ReviewTag>
          )
        )}
      </TopTags>
    </Section>
  );
}

const Section = styled.section({
  "& > div": {
    display: "flex",
    flexDirection: "column",
    gap: "2rem",
    margin: "2rem 0 4rem",
    "@media (min-width: 500px)": {
      flexDirection: "row",
      gap: "1rem",
    },
    "@media (min-width: 800px)": {
      flexDirection: "column",
      gap: "2rem",
      marginBottom: 0,
    },
  },
});
const ReactionList = styled.ul(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: 10,
  listStyleType: "none",
  margin: 0,
  maxWidth: 300,
  padding: 0,
  li: {
    display: "flex",
    alignItems: "center",
    p: {
      margin: 0,
    },
    strong: {
      display: "block",
      fontWeight: "bold",
    },
  },
  "&>li::before": {
    content: '"\\200B"',
    position: "absolute",
  },
}));

interface OrderProps {
  order?: number;
}

const ReactionItem = styled.li<OrderProps>(({ order, theme }) => ({
  alignItems: "center",
  display: "flex",
  gap: 8,
  position: "relative",
  order,
  lineHeight: "1.1",
  "&>div": {
    flex: 1,
  },
}));

const SH3 = styled(H3)(({ theme }) => ({
  ...theme.fonts.body,
  fontSize: theme.fontSizes.xs,
  fontWeight: "bold",
}));

const TopTags = styled.div({
  alignItems: "start",
  display: "flex",
  flex: "auto",
  gap: "10px !important",
});

const RatingBar = styled.div<{ value: number; max: number }>(
  ({ value, max, theme }) => ({
    borderRadius: theme.borderRadius.sm,
    backgroundColor: theme.colors.gray10,
    flex: 1,
    height: 16,
    marginBottom: 2,
    position: "relative",
    "&::before, &::after": {
      alignItems: "center",
      display: "flex",
      height: 16,
      position: "absolute",
      left: 0,
      width: value ? `${(value / max) * 100}%` : 0,
      borderRadius: theme.borderRadius.sm,
    },
    "&::before": {
      background: theme.colors.orange,
      content: "''",
    },
    // "&::after": {
    //   content: `"${value}"`,
    //   fontWeight: "bold",
    //   fontSize: theme.fontSizes.xs,
    //   justifyContent: "center",
    //   overflow: "hidden",
    //   textOverflow: "clip",
    //   whiteSpace: "nowrap",
    // },
  })
);

export { ReviewStats };
